import React, { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { LoanShell } from '../components/loan/LoanShell';
import { DocumentRow } from '../components/documents/DocumentRow';
import { DocumentUploadSheet } from '../components/documents/DocumentUploadSheet';
import { ProgressRing } from '../components/ui/ProgressRing';
import { HelpPanel } from '../components/ui/HelpPanel';
import { usePortal } from '../contexts/PortalContext';
import type { DocumentItem } from '../types/application';
export function Documents() {
  const {
    documents
  } = usePortal();
  const [active, setActive] = useState<DocumentItem | null>(null);
  const [showDone, setShowDone] = useState(false);
  const todo = documents.filter(d => d.status === 'attention' || d.status === 'needed').sort((a, b) => Number(b.status === 'attention') - Number(a.status === 'attention'));
  const inReview = documents.filter(d => d.status === 'uploaded' || d.status === 'reviewing');
  const done = documents.filter(d => d.status === 'accepted');
  const pct = Math.round((documents.length - todo.length) / documents.length * 100);
  return <LoanShell data-id="element-912" data-mp="8g16w8ainyqj">
      <main className="mx-auto max-w-4xl px-5 pt-8 md:px-8 md:pt-14" data-id="element-913" data-mp="8gj0ex5snyur">
        <div className="flex items-center gap-6" data-id="element-914" data-mp="deub1a3wyde1">
          <ProgressRing value={pct} size={84} stroke={7} data-id="element-915" data-mp="dfm8kqh2ml6y">
            <span className="tabular text-lg font-semibold text-ink-900" data-id="element-916" data-mp="114onhqkblzxr">
              {documents.length - todo.length}/{documents.length}
            </span>
          </ProgressRing>
          <div className="min-w-0" data-id="element-917" data-mp="1615in4pxtekc">
            <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.03em] text-ink-900 md:text-[36px]" data-id="element-918" data-mp="s4ibk949t80p">Your documents</h1>
            <p className="mt-1 text-ink-500" data-id="element-919" data-mp="w3is1kz9zykj">{todo.length ? `${todo.length} left to upload — most take under 2 minutes.` : 'Everything’s uploaded. Nice work.'}</p>
          </div>
        </div>

        {todo.length > 0 && <section className="mt-10" aria-labelledby="todo-title" data-id="element-920" data-mp="3gt87o07szoea">
            <h2 id="todo-title" className="mb-3 text-sm font-medium text-ink-900" data-id="element-921" data-mp="1ncavkojatvl0">
              To upload
            </h2>
            <ul className="divide-y divide-ink-100 overflow-hidden rounded-3xl bg-white ring-1 ring-ink-200" data-id="element-922" data-mp="1ig3m4lrp21yb">
              <AnimatePresence initial={false} data-id="element-923" data-mp="18nxwhiowzwma">
                {todo.map(d => <DocumentRow key={d.id} doc={d} onUpload={setActive} data-id="element-924" data-mp="18lv5avc0zxfz" />)}
              </AnimatePresence>
            </ul>
          </section>}

        {inReview.length > 0 && <section className="mt-10" aria-labelledby="review-title" data-id="element-925" data-mp="zpm8zpjchey2">
            <h2 id="review-title" className="mb-1 text-sm font-medium text-ink-900" data-id="element-926" data-mp="uooqfbufaiw1">
              With your loan team
            </h2>
            <p className="mb-3 text-sm text-ink-500" data-id="element-927" data-mp="2p27k2alc1hc2">Nothing to do here — we’ll let you know once they’re accepted.</p>
            <ul className="divide-y divide-ink-100 overflow-hidden rounded-3xl bg-white ring-1 ring-ink-200" data-id="element-928" data-mp="2k2l7m3dbw54b">
              {inReview.map(d => <DocumentRow key={d.id} doc={d} onUpload={setActive} data-id="element-929" data-mp="2sjvac0qpo5ih" />)}
            </ul>
          </section>}

        {done.length > 0 && <section className="mt-10" aria-labelledby="done-title" data-id="element-930" data-mp="31c5lbk9fkf0s">
            <button onClick={() => setShowDone(s => !s)} aria-expanded={showDone} className="flex items-center gap-2 text-sm font-medium text-ink-900" data-id="element-931" data-mp="36bigqtedauez">
              <span id="done-title" data-id="element-932" data-mp="29mu1u5xy7n9s">Accepted ({done.length})</span>
              <ChevronDownIcon className={`h-4 w-4 text-ink-400 transition-transform duration-200 ${showDone ? 'rotate-180' : ''}`} aria-hidden data-id="element-933" data-mp="2dm2vlasznl5c" />
            </button>
            <AnimatePresence initial={false} data-id="element-934" data-mp="2ioad0fwl7tf2">
              {showDone && <motion.ul initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.22
          }} className="mt-3 divide-y divide-ink-100 overflow-hidden rounded-3xl bg-white ring-1 ring-ink-200" data-id="element-935" data-mp="2nmbx945r1ys4">
                  {done.map(d => <DocumentRow key={d.id} doc={d} onUpload={setActive} data-id="element-936" data-mp="1btugt5wpu9i4" />)}
                </motion.ul>}
            </AnimatePresence>
          </section>}

        <div className="mt-10 grid gap-2 pb-16 md:max-w-xl" data-id="element-937" data-mp="1kqbsca8zs9ee">
          <HelpPanel question="Why these documents?" data-id="element-938" data-mp="1fsa8vo7nm82e">
            Your list is built from your application — your job, accounts, and the home you’re buying. If something changes, the list updates automatically.
          </HelpPanel>
          <HelpPanel question="Can I just take photos with my phone?" data-id="element-939" data-mp="1pr9cgsdkbdij">
            Yes. Make sure all four corners are visible and the text is readable. For multi-page statements, add each page — we’ll combine them for you.
          </HelpPanel>
        </div>
      </main>
      <DocumentUploadSheet doc={active} onClose={() => setActive(null)} data-id="element-940" data-mp="2dhvhsbw6mjou" />
    </LoanShell>;
}